/* I PARAMETRI A CONFRONTO.
 *
 * Quando due configurazioni della STESSA strategia differiscono per un solo
 * valore, quello è esattamente il punto in cui si sta osservando l'effetto di
 * quel parametro — ed è l'informazione più utile di tutta la pagina: dice che
 * la differenza di risultato ha una causa sola e quale sia. Per questo la
 * differenza non è una riga fra le altre: quando è unica, ha un riquadro suo.
 *
 * Fra STRATEGIE DIVERSE i parametri non sono confrontabili e basta: il «periodo»
 * di una media mobile e il «periodo» di un RSI hanno lo stesso nome e non
 * misurano la stessa cosa. Affiancare due colonne di numeri omonimi
 * suggerirebbe un paragone che non esiste. Qui si dice, e i parametri si
 * mostrano lo stesso — uno sotto l'altro, ognuno col suo candidato — perché
 * restano quello che serve per rifare l'esperimento.
 */

import { Avvertenza } from '../../componenti';
import { timeframe } from '../../lib/formato';
import type { Candidato, RigaParametro } from './confronto';
import { confrontaParametri } from './confronto';
import './confronto.css';

export function ConfrontoParametri({ candidati }: { candidati: Candidato[] }) {
  const c = confrontaParametri(candidati);

  if (!c.accostabili) {
    return (
      <div className="sb-cf-sezione__corpo">
        <Avvertenza livello="nota">
          Strategie diverse ({c.strategie.join(', ')}): i parametri non sono accostabili. Nomi
          uguali su strategie diverse non misurano la stessa cosa, e due colonne allineate
          suggerirebbero un paragone che non esiste. Restano qui sotto, uno per candidato, perché
          servono a rifare l'esperimento.
        </Avvertenza>

        {candidati.map((cand) => {
          const parametri = Object.entries(cand.dato.strategyParams ?? {});
          return (
            <div key={cand.id} className="sb-cf-param-blocco">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sb-s-3)' }}>
                <span className="sb-cf-lettera sb-cf-lettera--piccola">{cand.lettera}</span>
                <span className="sb-cf-param-diff__titolo">{cand.dato.strategyName}</span>
                <span className="sb-cf-param-esecuzione">
                  candela {timeframe(cand.dato.candleSize)} · warmup {cand.dato.warmupPeriod}
                </span>
              </div>
              {parametri.length === 0 ? (
                <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                  Questa riga non porta i parametri di strategia: non è un insieme vuoto, è un
                  campo assente, e senza di esso la configurazione non è riproducibile.
                </div>
              ) : (
                <div className="sb-bt-parametri" style={{ marginTop: 'var(--sb-s-4)' }}>
                  {parametri.map(([k, v]) => (
                    <div className="sb-bt-parametro" key={k}>
                      <div className="sb-bt-parametro__chiave">{k}</div>
                      <div className="sb-bt-parametro__valore">
                        {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /* --- stessa strategia: le colonne hanno senso --- */
  const soloEsecuzione = c.differenze.every((r) => r.esecuzione);

  return (
    <div className="sb-cf-sezione__corpo">
      {c.differenze.length === 0 && (
        <Avvertenza livello="nota">
          Le configurazioni sono <b>identiche</b>: stessa strategia, stessi parametri, stessa
          candela, stesso warmup. Se i risultati differiscono, la differenza sta altrove — nel
          periodo misurato o nei costi di esecuzione con cui sono state calcolate — e non in come
          la strategia è impostata.
        </Avvertenza>
      )}

      {c.differenze.length === 1 && (
        <div className="sb-cf-param-diff">
          <div className="sb-cf-param-diff__titolo">
            Cambia un solo valore: <code>{c.differenze[0].chiave}</code>
          </div>
          <div className="sb-cf-param-diff__testo">
            {c.differenze[0].valori
              .map((v, i) => `«${candidati[i].lettera}» ${v ?? 'assente'}`)
              .join(' · ')}
            . Tutto il resto coincide, quindi ogni differenza di risultato che vedi in questa
            pagina è l'effetto di{' '}
            {c.differenze[0].esecuzione
              ? 'questa sola scelta di esecuzione'
              : 'questo solo parametro'}
            {soloEsecuzione && c.differenze[0].chiave.startsWith('candela')
              ? ' — cioè della frequenza con cui la strategia guarda il mercato, non di come decide.'
              : '.'}
          </div>
        </div>
      )}

      {c.differenze.length > 1 && (
        <div className="sb-cf-nota" style={{ marginBottom: 'var(--sb-s-6)' }}>
          {c.differenze.length} valori diversi su {c.righe.length}: con più di una differenza il
          risultato non si può attribuire a un parametro solo. Per isolarne uno serve una coppia
          che cambi solo quello.
        </div>
      )}

      <TabellaParametri candidati={candidati} righe={c.righe} />
    </div>
  );
}

function TabellaParametri({
  candidati,
  righe,
}: {
  candidati: Candidato[];
  righe: RigaParametro[];
}) {
  /* Il `min-width` viene dal NUMERO DI CANDIDATI, non dalla larghezza dello
     schermo: è un conto sui dati, e a decidere se la tabella scorre resta il
     contenitore. */
  const larghezzaMinima = 150 + candidati.length * 130;

  return (
    <div className="sb-scorri-x">
      <table className="sb-cf-anni" style={{ minWidth: larghezzaMinima }}>
        <thead>
          <tr>
            <th>Parametro</th>
            {candidati.map((c) => (
              <th key={c.id}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="sb-cf-lettera sb-cf-lettera--piccola">{c.lettera}</span>#{c.id}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {righe.map((r) => (
            <tr key={r.chiave} className={r.diverso ? 'sb-cf-anno--comune' : undefined}>
              <td>
                <span className="sb-cf-param-chiave">{r.chiave}</span>
                {r.esecuzione && (
                  <span className="sb-cf-param-esecuzione" style={{ display: 'block' }}>
                    esecuzione, non strategia
                  </span>
                )}
              </td>
              {r.valori.map((v, i) => (
                <td
                  key={candidati[i].id}
                  style={{
                    color: r.diverso ? 'var(--sb-testo)' : 'var(--sb-testo-tenue)',
                    fontWeight: r.diverso ? 'var(--sb-peso-forte)' : 'var(--sb-peso-medio)',
                  }}
                >
                  {v ?? 'assente'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
